/**
 * Shared API infrastructure for admin client (Step 7.1).
 *
 * Contains: base URL, auth headers, pagination normalizer, error parsers.
 * Domain modules (units.js, tenancies.js, etc.) import from this file.
 */
import { API_BASE_URL, getApiHeaders, getApiHeadersMultipart } from "../../config";

export { API_BASE_URL, getApiHeaders, getApiHeadersMultipart };

export function expectPaginatedItems(data, endpointLabel) {
  if (data != null && typeof data === "object" && Array.isArray(data.items)) {
    return data.items;
  }
  throw new Error(
    `Ungültige Antwort von ${endpointLabel}: erwartet paginierte Antwort mit "items".`
  );
}

function detailItemToMessage(d) {
  if (typeof d === "string") return d;
  if (d && typeof d === "object") {
    if (typeof d.msg === "string" && d.msg) return d.msg;
    if (typeof d.message === "string" && d.message) return d.message;
    const ctx = d.ctx;
    if (ctx && typeof ctx === "object") {
      if (typeof ctx.reason === "string" && ctx.reason) return ctx.reason;
      if (typeof ctx.error === "string" && ctx.error) return ctx.error;
    }
  }
  return "";
}

export function parseAdminErrorBodyText(text) {
  try {
    const j = JSON.parse(text);
    if (Array.isArray(j.detail)) {
      const joined = j.detail
        .map(detailItemToMessage)
        .filter(Boolean)
        .join(" ");
      if (joined) return joined;
    }
    if (typeof j.detail === "string") return j.detail;
    if (j.detail && typeof j.detail === "object" && !Array.isArray(j.detail)) {
      const m = detailItemToMessage(j.detail);
      if (m) return m;
    }
  } catch (_) {
    /* ignore */
  }
  return text || "Die Anfrage ist fehlgeschlagen.";
}

export function parseAdminErrorFromText(text, status, statusText = "") {
  const st = statusText ? ` ${statusText}` : "";
  const httpFallback = `HTTP ${status}${st}`;
  let t = String(text ?? "").replace(/^\uFEFF/, "");
  t = t.trim();
  if (!t) {
    return httpFallback;
  }

  try {
    const j = JSON.parse(t);
    if (j != null && typeof j === "object" && !Array.isArray(j)) {
      const d = j.detail;
      if (typeof d === "string") {
        return d.trim();
      }
      if (Array.isArray(d)) {
        const joined = d
          .map(detailItemToMessage)
          .filter(Boolean)
          .join(" ")
          .trim();
        if (joined) return joined;
      } else if (d != null && typeof d === "object") {
        const m = detailItemToMessage(d).trim();
        if (m) return m;
      } else if (d != null && typeof d !== "object") {
        return String(d).trim();
      }
    }
  } catch (_) {
    /* not JSON */
  }

  return t || httpFallback;
}

export async function parseAdminErrorResponse(res) {
  let bodyRes = res;
  try {
    if (typeof res.clone === "function" && !res.bodyUsed) {
      bodyRes = res.clone();
    }
  } catch (_) {
    bodyRes = res;
  }
  let text;
  try {
    text = await bodyRes.text();
  } catch (e) {
    const m = e && e.message;
    if (typeof m === "string" && m.includes("body stream already read")) {
      return `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`;
    }
    throw new Error(String(e?.message ?? e));
  }
  return parseAdminErrorFromText(text, res.status, res.statusText);
}


/**
 * An Error that keeps the SERVER'S OWN failure code (RESP2d).
 *
 * ``parseAdminErrorResponse`` returns a human sentence, which is all a
 * toast needs. Some refusals need more than a toast: a category that was
 * deactivated while a form was open has to refetch the catalog and put
 * the person back in the picker, and a category that never existed does
 * not. Telling those apart from the German text would mean matching on a
 * sentence somebody is free to improve — so the code travels instead.
 *
 * Additive by construction: ``err.message`` is byte-identical to what
 * the old ``new Error(await parseAdminErrorResponse(res))`` produced, so
 * every existing caller behaves exactly as before and only the ones that
 * look for ``err.code`` see anything new.
 */
export async function adminErrorFrom(res) {
  let text = "";
  try {
    const clone =
      typeof res.clone === "function" && !res.bodyUsed ? res.clone() : res;
    text = await clone.text();
  } catch (_) {
    text = "";
  }
  const err = new Error(
    parseAdminErrorFromText(text, res.status, res.statusText)
  );
  err.status = res.status;
  try {
    const detail = JSON.parse(text)?.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      if (detail.code) err.code = detail.code;
      if (detail.details) err.details = detail.details;
    }
  } catch (_) {
    // A non-JSON body simply carries no code — the message still stands.
  }
  return err;
}

export function sanitizeClientErrorMessage(message, fallback) {
  if (!message || typeof message !== "string") return fallback || "";
  const m = message.trim();
  if (!m) return fallback || "";
  if (/^(TypeError|ReferenceError|SyntaxError|RangeError|Error):/.test(m)) {
    return fallback || "Ein Fehler ist aufgetreten.";
  }
  if (/fetch\s*(failed|error|aborted)/i.test(m)) {
    return "Netzwerkfehler: Server ist nicht erreichbar.";
  }
  if (/unexpected token/i.test(m) || /JSON/i.test(m)) {
    return fallback || "Serverfehler: ungültige Antwort erhalten.";
  }
  if (m.length > 400) {
    return m.slice(0, 200) + "…";
  }
  return m;
}

export function normalizeFetchError(e, fallbackMessage) {
  if (!e) return { message: fallbackMessage || "Unbekannter Fehler" };
  const raw =
    typeof e === "string" ? e : e?.message || e?.detail || String(e);
  return { message: sanitizeClientErrorMessage(raw, fallbackMessage) };
}
